import React, { useState, useMemo, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  IconButton, 
  Button,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  DialogTitle,
  DialogContent,
  Stack,
  Grid
} from '@mui/joy';
import {
  CaretLeft as ChevronLeftIcon,
  CaretRight as ChevronRightIcon,
  Calendar as TodayIcon,
  Clock as ScheduleIcon,
  CheckCircle as PublishedIcon,
  PencilSimple as EditIcon,
  FacebookLogo as FacebookIcon,
  InstagramLogo as InstagramIcon,
  Buildings as BusinessIcon
} from '@phosphor-icons/react';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, parseISO } from 'date-fns';

interface SocialMediaPost {
  id: number;
  title: string;
  content: string;
  scheduled_time: string;
  status: 'draft' | 'scheduled' | 'published';
  platforms_detail: Array<{
    id: number;
    platform: string;
    platform_display: string;
  }>;
}

interface SocialMediaCalendarProps {
  posts: SocialMediaPost[];
  onCreatePost: (date: Date) => void;
  onRangeChange?: (start: string, end: string) => void;
  onEditPost: (post: SocialMediaPost) => void;
}

export const SocialMediaCalendar: React.FC<SocialMediaCalendarProps> = ({ 
  posts, 
  onCreatePost,
  onEditPost,
  onRangeChange
}) => {
  const [agenda, setAgenda] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedPost, setSelectedPost] = useState<SocialMediaPost | null>(null);

  // Calculate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(currentDate);
    
    // Get the first day of the week containing the month start
    const calendarStart = new Date(monthStart);
    calendarStart.setDate(monthStart.getDate() - monthStart.getDay());
    
    // Get the last day of the week containing the month end
    const calendarEnd = new Date(monthEnd);
    calendarEnd.setDate(monthEnd.getDate() + (6 - monthEnd.getDay()));
    
    return eachDayOfInterval({ start: calendarStart, end: calendarEnd });
  }, [currentDate]);

  const rangeStart = format(calendarDays[0], 'yyyy-MM-dd');
  const rangeEnd = format(calendarDays[calendarDays.length - 1], 'yyyy-MM-dd');
  useEffect(() => { onRangeChange?.(rangeStart, rangeEnd); }, [rangeStart, rangeEnd, onRangeChange]);

  // Group posts by date
  const postsByDate = useMemo(() => {
    const grouped: Record<string, SocialMediaPost[]> = {};
    
    posts?.forEach(post => {
      if (post.scheduled_time) {
        const dateKey = format(parseISO(post.scheduled_time), 'yyyy-MM-dd');
        if (!grouped[dateKey]) {
          grouped[dateKey] = [];
        }
        grouped[dateKey].push(post);
      }
    });
    
    return grouped;
  }, [posts]);

  const handlePreviousMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
  };

  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getPlatformIcon = (platform: string) => {
    switch(platform) {
      case 'facebook':
        return <FacebookIcon size={16} />;
      case 'instagram':
        return <InstagramIcon size={16} />;
      case 'google_business_profile':
        return <BusinessIcon size={16} />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string) => {
    switch(status) {
      case 'published':
        return 'success';
      case 'scheduled':
        return 'primary';
      case 'draft':
        return 'neutral';
      default:
        return 'neutral';
    }
  };

  const getStatusIcon = (status: string) => {
    switch(status) {
      case 'published':
        return <PublishedIcon size={14} />;
      case 'scheduled':
        return <ScheduleIcon size={14} />;
      default:
        return null;
    }
  };

  return (
    <Box>
      {/* Calendar Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap:'wrap', gap:1, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <IconButton aria-label="Previous month" onClick={handlePreviousMonth}>
            <ChevronLeftIcon />
          </IconButton>
          <Typography level="h3" sx={{ minWidth: {xs:140,sm:200}, fontSize:{xs:'lg',sm:'xl2'}, textAlign: 'center' }}>
            {format(currentDate, 'MMMM yyyy')}
          </Typography>
          <IconButton aria-label="Next month" onClick={handleNextMonth}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
        <Button
          startDecorator={<TodayIcon />}
          variant="outlined"
          color="primary"
          onClick={handleToday}
        >
          Today
        </Button>
      </Box>

      <Button variant="plain" onClick={()=>setAgenda(value=>!value)} sx={{display:{xs:'none',md:'inline-flex'},mb:2}}>{agenda?'Show calendar':'Show agenda'}</Button>
      <Stack spacing={1} sx={{display:{xs:'flex',md:agenda?'flex':'none'}}}>
        {!posts.length && <Typography>No scheduled posts in this date range.</Typography>}
        {[...posts].sort((a,b)=>(a.scheduled_time||'').localeCompare(b.scheduled_time||'')).map(post=><Card key={post.id} onClick={()=>setSelectedPost(post)} sx={{cursor:'pointer'}}>
          <Typography level="title-sm">{post.title}</Typography>
          <Typography level="body-sm">{post.scheduled_time?format(parseISO(post.scheduled_time),'PPpp'):'Unscheduled'} · {post.status.replace('_',' ')}</Typography>
          <Button size="sm" variant="plain" onClick={(event)=>{event.stopPropagation();setSelectedPost(post);}}>View post</Button>
        </Card>)}
      </Stack>
      {/* Calendar Grid */}
      <Card variant="outlined" sx={{display:{xs:'none',md:agenda?'none':'block'}}}>
        <CardContent sx={{ p: 0 }}>
          {/* Weekday Headers */}
          <Grid container sx={{ borderBottom: 1, borderColor: 'divider' }}>
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
              <Grid 
                key={day} 
                xs={12/7}
                sx={{ 
                  p: 1, 
                  textAlign: 'center',
                  bgcolor: 'background.level1' 
                }}
              >
                <Typography level="body-sm" fontWeight="lg">
                  {day}
                </Typography>
              </Grid>
            ))}
          </Grid>

          {/* Calendar Days */}
          <Grid container>
            {calendarDays.map((day, index) => {
              const dateKey = format(day, 'yyyy-MM-dd');
              const dayPosts = postsByDate[dateKey] || [];
              const isCurrentMonth = isSameMonth(day, currentDate);
              const isTodayDate = isToday(day);

              return (
                <Grid
                  key={index}
                  xs={12/7}
                  sx={{
                    minHeight: 120,
                    border: 1,
                    borderColor: 'divider',
                    borderTop: 0,
                    borderLeft: index % 7 === 0 ? 1 : 0,
                    p: 1,
                    bgcolor: isCurrentMonth ? 'background.body' : 'background.level1',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isCurrentMonth ? 'background.level1' : 'background.level2'
                    }
                  }}
                  onClick={() => onCreatePost(day)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography
                      level="body-sm"
                      sx={{
                        fontWeight: isTodayDate ? 'lg' : 'md',
                        color: isCurrentMonth ? 'text.primary' : 'text.tertiary',
                        ...(isTodayDate && {
                          bgcolor: 'primary.main',
                          color: 'primary.contrastText',
                          px: 1,
                          borderRadius: 'sm'
                        })
                      }}
                    >
                      {format(day, 'd')}
                    </Typography>
                    {dayPosts.length > 0 && (
                      <Chip
                        size="sm"
                        variant="soft"
                        color="primary"
                      >
                        {dayPosts.length}
                      </Chip>
                    )}
                  </Box>

                  {/* Post Previews */}
                  <Stack spacing={0.5}>
                    {dayPosts.slice(0, 3).map((post) => (
                      <Box
                        key={post.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedPost(post);
                        }}
                        sx={{
                          p: 0.5,
                          bgcolor: 'background.level2',
                          borderRadius: 'xs',
                          cursor: 'pointer',
                          '&:hover': {
                            bgcolor: 'background.level3'
                          }
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {getStatusIcon(post.status)}
                          <Typography
                            level="body-xs"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              flex: 1
                            }}
                          >
                            {post.title}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          {post.platforms_detail?.slice(0, 3).map((platform) => (
                            <Box key={platform.id} sx={{ opacity: 0.7 }}>
                              {getPlatformIcon(platform.platform)}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ))}
                    {dayPosts.length > 3 && (
                      <Typography level="body-xs" sx={{ color: 'text.tertiary', textAlign: 'center' }}>
                        +{dayPosts.length - 3} more
                      </Typography>
                    )}
                  </Stack>
                </Grid>
              );
            })}
          </Grid>
        </CardContent>
      </Card>

      {/* Post Detail Modal */}
      <Modal open={!!selectedPost} onClose={() => setSelectedPost(null)}>
        <ModalDialog size="lg">
          <ModalClose />
          <DialogTitle>Post Details</DialogTitle>
          <DialogContent>
            {selectedPost && (
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography level="h4">{selectedPost.title}</Typography>
                  <Chip
                    color={getStatusColor(selectedPost.status)}
                    variant="soft"
                    startDecorator={getStatusIcon(selectedPost.status)}
                  >
                    {selectedPost.status}
                  </Chip>
                </Box>

                <Typography level="body-sm">
                  Scheduled: {format(parseISO(selectedPost.scheduled_time), 'PPpp')}
                </Typography>

                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Platforms:</Typography>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {selectedPost.platforms_detail?.map((platform) => (
                      <Chip
                        key={platform.id}
                        variant="outlined"
                        startDecorator={getPlatformIcon(platform.platform)}
                      >
                        {platform.platform_display}
                      </Chip>
                    ))}
                  </Box>
                </Box>

                <Box>
                  <Typography level="title-sm" sx={{ mb: 1 }}>Content:</Typography>
                  <Card variant="soft" size="sm">
                    <CardContent>
                      <Typography level="body-sm">
                        {selectedPost.content}
                      </Typography>
                    </CardContent>
                  </Card>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setSelectedPost(null)}
                  >
                    Close
                  </Button>
                  <Button
                    startDecorator={<EditIcon />}
                    disabled={selectedPost.status === 'published'}
                    onClick={() => {
                      onEditPost(selectedPost);
                      setSelectedPost(null);
                    }}
                  >
                    Edit Post
                  </Button>
                </Box>
              </Stack>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};